// @ts-nocheck
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { TimesheetEntry } from '../types';
import { getWeekNumber } from './timesheetValidation';
import { parseLocalDate } from './dateUtils';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function isWeekend(dateStr: string): boolean {
  const day = parseLocalDate(dateStr).getDay();
  return day === 0 || day === 6;
}

function formatDate(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
}

function formatTime(t: string | null): string {
  if (!t) return '';
  return String(t).substring(0, 5);
}

function formatHoursMinutes(decimal: number): string {
  const hours = Math.floor(decimal);
  const mins = Math.round((decimal - hours) * 60);
  return `${hours}:${String(mins).padStart(2, '0')}`;
}

function formatBreak(breakMinutes: number): string {
  if (breakMinutes === 60) return '1:00 hr';
  if (breakMinutes === 0) return '0:00';
  return `${breakMinutes} min`;
}

interface TimesheetPDFOptions {
  employeeName: string;
  clientName: string;
  jobTitle?: string | null;
  month: number;
  year: number;
  status: string;
  expectedDailyHours: number;
  totalHours: number;
  entries: TimesheetEntry[];
  weekFilter?: [number, number]; // e.g. [1, 2] for Week 01 & 02
  returnBlob?: boolean; // if true, return Blob instead of downloading
  dateRange?: { from: string; to: string }; // custom date range mode
  isIndirect?: boolean; // indirect clients get purple theme + intermediary branding
}

export default function generateTimesheetPDF(options: TimesheetPDFOptions): { blob: Blob; filename: string } | null {
  const {
    employeeName, clientName, jobTitle, month, year,
    totalHours, entries, weekFilter, dateRange, isIndirect,
  } = options;

  // Color scheme: blue for direct, purple for indirect
  const primaryColor: [number, number, number] = isIndirect ? [109, 40, 217] : [37, 99, 235]; // #6D28D9 vs #2563EB
  const summaryRowColor: [number, number, number] = isIndirect ? [237, 233, 254] : [232, 245, 233]; // #EDE9FE vs #e8f5e9
  const summaryTextColor: [number, number, number] = isIndirect ? [109, 40, 217] : [46, 125, 50]; // purple vs green
  const brandName = isIndirect ? clientName : 'Kovil AI';

  // Filter entries by week if specified (skip for dateRange — already filtered by API)
  const filteredEntries = dateRange
    ? entries
    : weekFilter
      ? entries.filter(e => {
          const wn = getWeekNumber(e.entry_date);
          return wn >= weekFilter[0] && wn <= weekFilter[1];
        })
      : entries;

  // Calculate filtered total
  const filteredTotal = (dateRange || weekFilter)
    ? filteredEntries.reduce((sum, e) => sum + Number(e.total_hours || 0), 0)
    : totalHours;

  const formatDateLabel = (isoStr: string) => {
    const d = parseLocalDate(isoStr);
    const day = d.getDate();
    const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
    return `${day} ${mon} ${d.getFullYear()}`;
  };

  const periodLabel = dateRange
    ? `${formatDateLabel(dateRange.from)} — ${formatDateLabel(dateRange.to)}`
    : weekFilter
      ? `${MONTHS[month - 1]} ${year} — Week ${String(weekFilter[0]).padStart(2, '0')} & ${String(weekFilter[1]).padStart(2, '0')}`
      : `${MONTHS[month - 1]} ${year}`;

  const totalLabel = (dateRange || weekFilter) ? 'Period Total' : 'Monthly Total';

  const doc = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // ── Header band ──
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 20, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(brandName, margin, 13);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Timesheet', pageWidth / 2, 13, { align: 'center' });

  doc.setFontSize(9);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, pageWidth - margin, 13, { align: 'right' });

  // ── Info section ──
  const infoY = 28;
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  // Left column
  doc.text('Employee:', margin, infoY);
  doc.setTextColor(30, 30, 30);
  doc.setFont('helvetica', 'bold');
  const employeeLabel = jobTitle ? `${employeeName}  —  Role: ${jobTitle}` : employeeName;
  doc.text(employeeLabel, margin + 22, infoY);

  if (!isIndirect) {
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text('Client:', margin, infoY + 6);
    doc.setTextColor(30, 30, 30);
    doc.setFont('helvetica', 'bold');
    doc.text(clientName, margin + 22, infoY + 6);
  }

  // Right column
  const rightX = pageWidth - margin;
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text(`Period: ${periodLabel}`, rightX, infoY, { align: 'right' });

  // Total
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text(`${totalLabel}: ${formatHoursMinutes(filteredTotal)}`, rightX, infoY + 6, { align: 'right' });

  // ── Divider ──
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(margin, infoY + 16, pageWidth - margin, infoY + 16);

  // ── Build table data with weekly summary rows ──
  const tableBody: any[] = [];
  let currentWeek: number | null = null;
  let weekTotalHours = 0;

  filteredEntries.forEach((entry, idx) => {
    const weekNum = getWeekNumber(entry.entry_date);
    const isOrphan = weekNum === 0;
    const weekLabel = isOrphan ? '' : `Week ${String(weekNum).padStart(2, '0')}`;

    // Insert summary row when transitioning to a new week
    if (currentWeek !== null && weekNum !== currentWeek && currentWeek > 0) {
      tableBody.push({
        isSummary: true,
        data: [
          '',
          `Week ${String(currentWeek).padStart(2, '0')} Total`,
          '',
          '',
          '',
          '',
          formatHoursMinutes(weekTotalHours),
        ],
      });
      weekTotalHours = 0;
    }

    if (!isOrphan) {
      weekTotalHours += Number(entry.total_hours || 0);
    }
    currentWeek = weekNum;

    const hrs = Number(entry.total_hours || 0);

    // Strip bold markers and replace separator characters for PDF
    const taskText = (entry.task_details || '')
      .replace(/[─━═]+/g, '------------------------------')
      .replace(/\*\*(.*?)\*\*/g, '$1');

    tableBody.push({
      isSummary: false,
      isWeekend: isWeekend(entry.entry_date),
      data: [
        weekLabel,
        formatDate(entry.entry_date),
        formatTime(entry.start_time),
        formatTime(entry.finish_time),
        taskText,
        formatBreak(entry.break_minutes ?? 0),
        hrs > 0 ? formatHoursMinutes(hrs) : '0:00',
      ],
    });

    // Final week summary
    if (idx === filteredEntries.length - 1 && weekNum > 0) {
      tableBody.push({
        isSummary: true,
        data: [
          '',
          `Week ${String(weekNum).padStart(2, '0')} Total`,
          '',
          '',
          '',
          '',
          formatHoursMinutes(weekTotalHours),
        ],
      });
    }
  });

  // ── Draw the table ──
  const tableStartY = infoY + 20;

  autoTable(doc, {
    startY: tableStartY,
    head: [['Week', 'Date', 'Start (IST)', 'Finish (IST)', 'Task Details', 'Break', 'Total Hours']],
    body: tableBody.map(row => row.data),
    theme: 'grid',
    showHead: 'everyPage',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
      halign: 'center',
      cellPadding: 3,
    },
    columnStyles: {
      0: { cellWidth: 22, halign: 'center', fontSize: 8 },
      1: { cellWidth: 30, fontSize: 8 },
      2: { cellWidth: 25, halign: 'center', fontSize: 8 },
      3: { cellWidth: 25, halign: 'center', fontSize: 8 },
      4: { cellWidth: 'auto', fontSize: 7.5, cellPadding: { top: 2, right: 3, bottom: 2, left: 3 } },
      5: { cellWidth: 22, halign: 'center', fontSize: 7 },
      6: { cellWidth: 24, halign: 'center', fontSize: 8, fontStyle: 'bold' },
    },
    styles: {
      lineColor: [200, 200, 200],
      lineWidth: 0.2,
      cellPadding: 2.5,
      overflow: 'linebreak',
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250],
    },
    didParseCell: (data: any) => {
      // Skip header rows — don't override their styles
      if (data.row.section === 'head') return;

      const rowIdx = data.row.index;
      if (rowIdx < tableBody.length) {
        const rowInfo = tableBody[rowIdx];

        // Weekly summary row styling
        if (rowInfo.isSummary) {
          data.cell.styles.fillColor = summaryRowColor;
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 9;
          if (data.column.index === 1 || data.column.index === 6) {
            data.cell.styles.textColor = summaryTextColor;
          }
        }

        // Weekend row styling
        if (rowInfo.isWeekend && !rowInfo.isSummary) {
          data.cell.styles.fillColor = [255, 248, 248]; // #fff8f8
          if (data.column.index === 1) {
            data.cell.styles.textColor = [211, 47, 47]; // #d32f2f
          }
        }
      }
    },
    didDrawCell: (data: any) => {
      if (data.row.section === 'head') {
        const cx = data.cell.x + data.cell.width / 2;
        const by = data.cell.y + data.cell.height - 1.5;
        if (data.column.index === 5) {
          doc.setFontSize(5.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(200, 210, 230);
          doc.text('(Non-Billable)', cx, by, { align: 'center' });
        }
        if (data.column.index === 6) {
          doc.setFontSize(5.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(200, 210, 230);
          doc.text('(Billable)', cx, by, { align: 'center' });
        }
      }
    },
    margin: { left: margin, right: margin },
  });

  // ── Monthly total footer ──
  const finalY = (doc as any).lastAutoTable.finalY + 8;
  doc.setFillColor(...primaryColor);
  doc.roundedRect(pageWidth - margin - 70, finalY, 70, 10, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`${totalLabel}: ${formatHoursMinutes(filteredTotal)}`, pageWidth - margin - 35, finalY + 7, { align: 'center' });

  // ── Footer ──
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`${brandName} Timesheet - Confidential`, margin, pageHeight - 6);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
  }

  // ── Save or Return ──
  const ordinalSuffix = (d: number) => {
    if (d >= 11 && d <= 13) return 'th';
    switch (d % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  const formatFilenameDate = (isoStr: string) => {
    const d = parseLocalDate(isoStr);
    const day = d.getDate();
    const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
    return `${day}${ordinalSuffix(day)} ${mon} ${d.getFullYear()}`;
  };

  let filename: string;
  if (dateRange) {
    filename = `Timesheet_${employeeName.replace(/\s+/g, '_')}_${formatFilenameDate(dateRange.from)} - ${formatFilenameDate(dateRange.to)}.pdf`;
  } else {
    const weekSuffix = weekFilter ? `_W${weekFilter[0]}-W${weekFilter[1]}` : '';
    filename = `Timesheet_${employeeName.replace(/\s+/g, '_')}_${MONTHS[month - 1]}_${year}${weekSuffix}.pdf`;
  }

  if (options.returnBlob) {
    return { blob: doc.output('blob'), filename };
  }

  doc.save(filename);
  return null;
}
